// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { MilestoneModel } from '../../models/Milestone';
import { createId } from '../../index';

export interface MilestoneFilters {
  /** Organization ID (optional - omit for cross-org queries in "View All" mode) */
  orgId?: string;
  /** Filter by workspace ID */
  workspaceId?: string;
  /** Filter by user ID */
  userId?: string;
  /** Filter by project ID */
  projectId?: string;
  /** Filter by goal ID */
  goalId?: string;
  /** Filter by completion state */
  completed?: boolean;
  /** Filter by archived state (default: false) */
  archived?: boolean;
}

export interface CreateMilestoneInput {
  /** Milestone name (kebab-case) */
  name: string;
  /** Milestone title */
  title: string;
  /** Milestone description */
  description: string;
  /** Organization ID */
  orgId: string;
  /** User ID of milestone creator */
  userId: string;
  /** Workspace ID */
  workspaceId: string;
  /** Parent project ID */
  projectId: string;
  /** Parent goal ID */
  goalId: string;
  /** Optional planned start date (ISO 8601) */
  startAt?: string;
  /** Optional actual start date (ISO 8601) */
  startedAt?: string;
  /** Optional due date (ISO 8601) */
  dueAt?: string;
  /** Optional completion date (ISO 8601) */
  completedAt?: string;
  /** Optional time budget in hours */
  timeBudgetHours?: number;
  /** Optional category ID */
  categoryId?: string;
  /** Optional tag IDs */
  tagIds?: string[];
  /** Optional sort order */
  sortOrder?: number;
}

export interface UpdateMilestoneInput {
  /** Milestone ID to update */
  id: string;
  /** Partial milestone data to update */
  updates: Partial<MilestoneModel>;
}

export class MilestoneOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: MilestoneFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<MilestoneModel> {
    const collection = this.db.collections.milestones;

    // Build selector - orgId is optional to support "View All" mode across organizations
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    // Only add orgId filter if provided (allows cross-org queries when not specified)
    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId };
    }

    if (filters.goalId) {
      selector.goalId = { $eq: filters.goalId };
    }

    // Use $ne: true for completed filter to match documents where completed is false, undefined, or missing
    if (filters.completed !== undefined) {
      selector.completed = filters.completed === true ? { $eq: true } : { $ne: true };
    }

    const queryOptions: QueryOptions<MilestoneModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<MilestoneModel>(collection, queryOptions);
  }

  async findOne(milestoneId: string): Promise<MilestoneModel | null> {
    const doc = await this.db.collections.milestones.findOne(milestoneId).exec();
    return doc ? (doc.toJSON() as MilestoneModel) : null;
  }

  async count(filters: MilestoneFilters): Promise<number> {
    const collection = this.db.collections.milestones;

    // Build selector - orgId is optional to support "View All" mode
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId };
    }

    if (filters.goalId) {
      selector.goalId = { $eq: filters.goalId };
    }

    // Use $ne: true for completed filter to match documents where completed is false, undefined, or missing
    if (filters.completed !== undefined) {
      selector.completed = filters.completed === true ? { $eq: true } : { $ne: true };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<MilestoneModel, CreateMilestoneInput> {
    return new MutationOperator<MilestoneModel, CreateMilestoneInput>(
      this.db.collections.milestones,
      {
        mutationFn: async (collection, input) => {
          const now = new Date().toISOString();
          const milestone: MilestoneModel = {
            id: createId('milestone'),
            orgId: input.orgId,
            userId: input.userId,
            workspaceId: input.workspaceId,
            projectId: input.projectId,
            goalId: input.goalId,
            name: input.name,
            title: input.title,
            description: input.description,
            completed: false,
            archived: false,
            sortOrder: input.sortOrder ?? 0,
            version: 1,
            createdAt: now,
            updatedAt: now,
            ...(input.startAt && { startAt: input.startAt }),
            ...(input.startedAt && { startedAt: input.startedAt }),
            ...(input.dueAt && { dueAt: input.dueAt }),
            ...(input.completedAt && { completedAt: input.completedAt }),
            ...(input.timeBudgetHours && { timeBudgetHours: input.timeBudgetHours }),
            ...(input.categoryId && { categoryId: input.categoryId }),
            ...(input.tagIds && { tagIds: input.tagIds }),
          };

          await collection.insert(milestone);
          return milestone;
        },
      }
    );
  }

  updateMutation(): MutationOperator<MilestoneModel, UpdateMilestoneInput> {
    return new MutationOperator<MilestoneModel, UpdateMilestoneInput>(
      this.db.collections.milestones,
      {
        mutationFn: async (collection, { id, updates }) => {
          const doc = await collection.findOne(id).exec();
          if (!doc) throw new Error(`Milestone ${id} not found`);

          await doc.update({
            $set: { ...updates, updatedAt: new Date().toISOString() },
          });

          return doc.toJSON() as MilestoneModel;
        },
      }
    );
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.milestones, {
      mutationFn: async (collection, milestoneId) => {
        const doc = await collection.findOne(milestoneId).exec();
        if (!doc) throw new Error(`Milestone ${milestoneId} not found`);

        await doc.update({
          $set: { archived: true, updatedAt: new Date().toISOString() },
        });
      },
    });
  }

  completeMutation(): MutationOperator<MilestoneModel, string> {
    return new MutationOperator<MilestoneModel, string>(
      this.db.collections.milestones,
      {
        mutationFn: async (collection, milestoneId) => {
          const doc = await collection.findOne(milestoneId).exec();
          if (!doc) throw new Error(`Milestone ${milestoneId} not found`);

          await doc.update({
            $set: { completed: true, updatedAt: new Date().toISOString() },
          });

          return doc.toJSON() as MilestoneModel;
        },
      }
    );
  }
}
